import React from "react";
import ZSLogo from "public/img/AskKural.svg?url";

export interface ICustomHumanChartPercentageRow {
	percentages: number[]; // [25, 35, 40]
	colors: string[]; // ["#3366cc", "#6699ff", "#999999"]
}

export interface ICustomHumanChartProps {
	data: ICustomHumanChartPercentageRow[];
}

// SVG for Human Icon
const HumanIcon: React.FC<{ color: string }> = ({ color }) => (
	<svg width="16" height="34" viewBox="0 0 17 37" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M12.016 3.65356C12.016 5.66611 10.3445 7.30711 8.29451 7.30711C6.213 7.30711 4.57302 5.63515 4.57302 3.62259C4.57302 1.61004 6.24453 0 8.29451 0C10.3445 0 12.016 1.641 12.016 3.65356Z"
			fill={color}
		/>
		<path
			d="M2.93304 13.128V21.4259C2.93304 23.5933 0 23.5933 0 21.395V12.7874C0 10.1556 1.63998 8.01925 4.32071 8.01925H12.6468C15.5483 8.01925 16.999 10.4033 16.999 12.8494V21.395C17.0621 23.6243 14.0975 23.6243 14.0975 21.395V13.0971H13.1514V34.9255C13.1514 36.0402 12.2052 36.969 11.0699 36.969C9.93449 36.969 8.98834 36.0402 8.98834 34.9255V22.9431H8.0422V34.9565C8.0422 36.0711 7.09606 37 5.96069 37C4.82532 37 3.87918 36.0711 3.87918 34.9565V13.128H2.93304Z"
			fill={color}
		/>
	</svg>
);

// Component to render percentage distribution
const CustomHumanChart: React.FC<ICustomHumanChartProps> = ({ data }) => {
	return (
		<div className="flex flex-col gap-8 items-center">
			{data.map((row, rowIndex) => {
				const totalPeople = 8;
				const [first, second, third] = row.percentages;

				// Handle 100% edge case
				if (first === 100) {
					const personArray = Array(totalPeople).fill(row.colors[0]);
					return (
						<div key={rowIndex} className="flex flex-col items-center gap-2">
							{/* Percentage Label */}
							<div className="flex gap-2">
								<span className="text-lg font-semibold" style={{ color: row.colors[0] }}>
									{first}%
								</span>
							</div>

							{/* Human SVGs */}
							<div className="flex gap-3">
								{personArray.map((color, i) => (
									<HumanIcon key={i} color={color} />
								))}
							</div>
						</div>
					);
				}

				// Calculate normal distribution if no 100% case
				const firstCount = Math.round((first / 100) * totalPeople);
				const remainingPeople = totalPeople - firstCount;

				const secondCount = Math.round((second / (second + third)) * remainingPeople);
				const thirdCount = totalPeople - (firstCount + secondCount);

				const personArray = [...Array(firstCount).fill(row.colors[0]), ...Array(secondCount).fill(row.colors[1]), ...Array(thirdCount).fill(row.colors[2])];

				return (
					<div key={rowIndex} className="flex flex-col items-center gap-2">
						{/* Percentage Labels */}
						{/* <div className="flex gap-2">
							{row.percentages.map((perc, i) =>
								perc > 0 ? (
									<span key={i} className="text-lg font-semibold" style={{ color: row.colors[i] }}>
										{perc}%
									</span>
								) : null
							)}
						</div> */}
						<div className="flex w-full">
							<div style={{ width: `${(firstCount / totalPeople) * 100}%` }} className="flex justify-center">
								{first > 0 && (
									<span className="text-lg font-semibold" style={{ color: row.colors[0] }}>
										{first}%
									</span>
								)}
							</div>
							<div style={{ width: `${(secondCount / totalPeople) * 100}%` }} className="flex justify-center">
								{second > 0 && (
									<span className="text-lg font-semibold" style={{ color: row.colors[1] }}>
										{second}%
									</span>
								)}
							</div>
							<div style={{ width: `${(thirdCount / totalPeople) * 100}%` }} className="flex justify-center">
								{third > 0 && (
									<span className="text-lg font-semibold" style={{ color: row.colors[2] }}>
										{third}%
									</span>
								)}
							</div>
						</div>

						{/* Human SVGs */}
						<div className="flex gap-3">
							{personArray.map((color, i) => (
								<HumanIcon key={i} color={color} />
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const HumanChart = ({ data }) => {
	const { chartData, chartTitle, n_size_value, colorLegends } = data;

	const selectedOption = [
		{
			name: "Aided",
			color: "#582C91",
		},
		{
			name: "Unaided",
			color: "#AE70FF",
		},
		{
			name: "Unaware",
			color: "#DDDDDD",
		},
	];
	//const waves = ["wave 1", "wave 2", "wave 3", "wave 4", "wave 5"];
	//const products = ["product_1", "product_2", "product_3"];
	//const n_size = ["n = 65 - 67", "n = 67 - 70", "n = 70 - 75", "n = 75 - 80", "n = 80 - 85"];

	// Extract necessary data from props

	// Extract product names from the first data point
	const productNames = Object.keys(chartData[0]).filter((key) => key !== "name");

	// Create selectedProducts array
	const selectedProducts = productNames.map((name) => ({
		name,
		color: colorLegends[name] || "#000000", // Use provided color or default to black
	}));

	// Create waves array
	const waves = chartData.map((item) => item.name.toLowerCase());

	// Transform data for productData
	const productColor = [
		["#39559E", "#5581F2", "#0000001A"],
		["#A93C8B", "#F356C7", "#0000001A"],
		["#3F83A2", "#53B3E0", "#0000001A"],
		["#3F83A2", "#53B3E0", "#0000001A"],
		["#3F83A2", "#53B3E0", "#0000001A"],
	];
	const productData = {};
	waves.forEach((wave, waveIndex) => {
		productData[wave] = {};
		productNames.forEach((product, productIndex) => {
			const value = chartData[waveIndex][product];
			productData[wave][productNames[productIndex]] = [
				{
					percentages: [
						Math.round(value * 100 * 0.3448275862), // Aided (approximation)
						Math.round(value * 100 * 0.4827586207), // Unaided (approximation)
						100 - Math.round(value * 100), // Unaware
					],
					//colors: ["#582C91", "#AE70FF", "#DDDDDD"], // Using default colors as they're not provided in the original data
					colors: productColor[productIndex], // Using default colors as they're not provided in the original data
				},
			];
		});
	});

	return (
		<>
			{/* <div className="flex items-center justify-start">
				<div className="text-[--secondary-color] mr-6 font-['Open_Sans'] font-semibold text-base-1 leading-[19.07px] tracking-[0.3px]">{data?.chartTitle}</div>

				{selectedOption.map((product) => (
					<div className="flex items-center" key={product.name}>
						<div className={`w-4 h-4 rounded-full ${product.color || "bg-red"} mr-1`} style={{ backgroundColor: product.color }} />
						<span className="text-base-12 text-gray-600  mr-5">{product.name}</span>
					</div>
				))}
			</div> */}
			<div className="flex items-center justify-center mt-4">
				{selectedProducts.map((product) => (
					<div className="flex items-center" key={product.name}>
						<div className={`w-3 h-5 rounded-full ${product.color || "bg-red"} mr-1`} style={{ backgroundColor: product.color }} />
						<span className="text-[10px] text-gray-600  mr-5">{product.name}</span>
					</div>
				))}
			</div>
			<div style={{ display: "flex", justifyContent: "space-between", marginLeft: -0, marginTop: -10, height: "auto" }}>
				{waves.map((wave, index) => (
					<>
						<div className="" style={{ display: "flex", flexDirection: "column" }}>
							<div style={{ marginLeft: "0px", marginTop: 30 }}>
								<span
									className={`${"text-[--primary-color-2] text-base-1 pr-4 pl-4 mb-4"}  pl-1 pr-1 text-center inline-block font-semibold`}
									style={{
										marginLeft: 80,
									}}>
									{index === waves?.length - 1 ? waves?.[index] : ""}
								</span>
								<div
									style={{
										marginLeft: 10,
									}}
									className={`${
										index === waves?.length - 1 ? "text-[--primary-color-2] text-[10.5px] pr-2 pl-2 mb-4" : "text-[#5679B2] text-base-1 pr-4 pl-4 mb-4"
									}  p-1  text-center border  rounded-[12px]  inline-block font-semibold`}>
									{index === waves?.length - 1 ? "Latest" : waves?.[index]}
								</div>
								{/* <div className="pl-16 pr-16 border-r-2 border-dashed transition-all duration-300 hover:bg-gradient-to-b hover:from-white hover:to-[#4cbb9650]"> */}
								<div
									className={`pl-16 pr-16 border-r-2 border-dashed`}
									style={{
										background: index === waves?.length - 1 ? "linear-gradient(0deg, var(--border-or-bg-color) 0%, #FFFFFF 100%)" : "",
										borderColor: index === waves?.length - 1 ? "var(--border-or-bg-color)" : "",
									}}>
									{productNames.map((product, i) => {
										return (
											<div className={`mt-8`}>
												<CustomHumanChart data={productData?.[wave]?.[product] || []} />
											</div>
										);
									})}
									<div className="w-full border-t-2 scale-x-150 origin-left mt-8  border-[--secondary-color-2-opacity-15]"></div>
								</div>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: "80px",
										height: "20px",
										backgroundColor: "#fff",
										// border: "2px solid #e2eaf1",
										borderRadius: "10px",
										color: "#5578b0",
										fontSize: "12px",
										textAlign: "center",
										marginLeft: 60,
										marginTop: 20,
										marginBottom: 40,
									}}>
									{n_size_value?.[index]}
								</div>
							</div>
						</div>
						<div className="h-full border-t-2 border-dashed mt-6 border-[--secondary-color-2-opacity-15] rotate-90"></div>
					</>
				))}
			</div>
			<div className="flex bg-[#f6fbfa] rounded-lg border-2 border-dashed border-[#d1ede3] p-4">
				<span className="flex items-center justify-center">
					<img src={String(ZSLogo)} alt="ZS Logo" className="m-2" style={{ maxWidth: "2.5rem" }} />
				</span>
				<p className="mt-2 font-sans text-[14px] leading-[23px] tracking-[0.3px]">{data?.footnote?.other_considerations}</p>
			</div>
		</>
	);
};

export default HumanChart;
